import { readRedirectReturn } from '@appercut/round-vue';
import { isApplePayDevice } from '../utils/walletAvailability';
import { HANDLED_RETURN_KEY } from '../common/constants/redirectReturn';
import { usePaymentError } from './usePaymentError';
import type { ApiErrorModel } from '~/common/models/api.models.ts';
import type { RedirectReturnPayload } from '../interfaces/redirectReturn.interface';
import type { RedirectMethod, RedirectReturnResult } from '../types/redirectReturn';

const RETURN_PARAMS = ['payment_id', 'status', 'payment_intent_client_secret'];
const INTENT_KEY = 'hyperswitch_pending_intent';

const IGNORED: RedirectReturnResult = { outcome: 'ignored' };

function rememberIntent(paymentId: string, payload: RedirectReturnPayload): void {
  if (!import.meta.client || !paymentId) return;
  sessionStorage.setItem(INTENT_KEY, JSON.stringify({ paymentId, ...payload }));
}

function readStoredIntent(): (RedirectReturnPayload & { paymentId?: string }) | null {
  try {
    return JSON.parse(sessionStorage.getItem(INTENT_KEY) ?? 'null');
  } catch {
    return null;
  }
}

function readRememberedPayload(paymentId: string): RedirectReturnPayload | null {
  const stored = readStoredIntent();

  return stored?.paymentId === paymentId ? stored : null;
}

function rememberMethod(method: RedirectMethod): void {
  if (!import.meta.client) return;

  const stored = readStoredIntent();
  if (!stored) return;

  sessionStorage.setItem(INTENT_KEY, JSON.stringify({ ...stored, method, startedAt: Date.now() }));
}

export function useHyperswitchRedirectReturn() {
  const API = useKegelApi();
  const { saveQuery } = useUrlQuery();
  const store = useKegelAnalyticStore();
  const { buyUpsale } = payments();
  const { gatewayType, roundNumber } = useHelpers();
  const { $paymentMethods, $logger } = useNuxtApp();
  const { reportPaymentError } = usePaymentError();
  const { track } = useEventsDispatcher();
  const { openError } = useAllModals();
  const { setLoader } = useDialogStore();

  const isApplePay = (): boolean => isApplePayDevice();

  function methodType(method?: RedirectMethod): string {
    if (method === 'card') return $paymentMethods.card;

    return isApplePay() ? $paymentMethods.applePay : $paymentMethods.googlePay;
  }

  function storedMethodName(method?: RedirectMethod): string {
    if (method === 'card') return 'card';

    return isApplePay() ? 'apple-pay' : 'google-pay';
  }

  async function finalise(paymentId: string, payload: RedirectReturnPayload): Promise<void> {
    if (payload.upsale) {
      const error = await buyUpsale({
        intent_id: paymentId,
        item: payload.item,
        web_user_uuid: store.userUuid,
        stage: 'upsale',
        amount: payload.amount,
      });

      if (error) throw error;
      return;
    }

    const response = await API.creditCardAccept({
      intent_id: paymentId,
      new_flow: true,
      name: '',
      item: payload.item,
    });

    if (response?.detail?.error) throw response.detail.error;
  }

  function reportError(error: unknown, payload: RedirectReturnPayload): void {
    openError({ content: 'lp_v7_pop_up_payment_error_message' });

    try {
      reportPaymentError({
        error: error as ApiErrorModel,
        errorCode: payload.method === 'card' ? 'Hyperswitch 3DS Error' : 'Wallet Error',
        stage: payload.upsale ? 'Upsale' : 'Subscription',
        type: methodType(payload.method),
      });
    } catch (reportingError: unknown) {
      $logger.error(reportingError as Error, { tags: { flow: 'payment', step: 'report_redirect_return' } });
    }
  }

  function secondsBetween(from: number, to: number): number | undefined {
    return roundNumber((to - from) / 1000) ?? undefined;
  }

  function trackWalletDuration(payload: RedirectReturnPayload, returnedAt: number, result: 'success' | 'error'): void {
    if (payload.method !== 'wallet' || !payload.startedAt) return;

    const now = Date.now();
    track('Payment duration', {
      type: methodType(payload.method),
      gateway: gatewayType.value,
      authorization_duration: secondsBetween(payload.startedAt, returnedAt),
      backend_payment_duration: secondsBetween(returnedAt, now),
      total_duration: secondsBetween(payload.startedAt, now),
      result,
    });
  }

  async function consumeRedirectReturn(fallback: RedirectReturnPayload): Promise<RedirectReturnResult> {
    if (!import.meta.client) return IGNORED;

    const returned = readRedirectReturn();
    if (!returned) return IGNORED;

    saveQuery(Object.fromEntries(RETURN_PARAMS.map(param => [param, undefined])));

    if (sessionStorage.getItem(HANDLED_RETURN_KEY) === returned.paymentId) return IGNORED;
    sessionStorage.setItem(HANDLED_RETURN_KEY, returned.paymentId);

    const remembered = readRememberedPayload(returned.paymentId);

    if (!remembered && fallback.upsale) {
      reportError(new Error(`no memo for returned payment ${returned.paymentId}`), fallback);
      return IGNORED;
    }

    const payload = remembered ?? fallback;
    const returnedAt = Date.now();

    if (!returned.authorized) {
      reportError(new Error(`payment returned status: ${returned.status ?? 'none'}`), payload);
      trackWalletDuration(payload, returnedAt, 'error');
      return remembered ? { outcome: 'refused', planCode: remembered.planCode } : IGNORED;
    }

    setLoader(true);

    try {
      await finalise(returned.paymentId, payload);
    } catch (error: unknown) {
      reportError(error, payload);
      trackWalletDuration(payload, returnedAt, 'error');
      return IGNORED;
    } finally {
      setLoader(false);
    }

    sessionStorage.setItem('payment_method', storedMethodName(payload.method));
    track('Authorization_Success', { type: methodType(payload.method), price: payload.discPrice });
    trackWalletDuration(payload, returnedAt, 'success');
    if (payload.method === 'card') {
      track('CC Payment Attempt', { type: methodType(payload.method), gateway: gatewayType.value });
    }

    return { outcome: 'finalised', belongsToThisForm: payload.upsale === fallback.upsale, planCode: payload.planCode };
  }

  return { rememberIntent, rememberMethod, consumeRedirectReturn, walletMethodType: () => methodType() };
}
